import { useEffect, useMemo, useRef } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';

import { alienAnimations } from '@/features/timeGame/constants';

import Alien, { AlienHandle } from '@/features/timeGame/components/elements/Alien';

import ConveyorBeltCounterSvg from '@/features/timeGame/assets/images/conveyor-belt-counter.svg?react';
import conveyorBeltAnimation from '@/features/timeGame/assets/lottie/conveyor-belt.json';

import '@/features/timeGame/styles/conveyor-belt.scss';

interface ConveyorBeltProps {
	correctAnswersCount: number;
	totalQuestions: number;
	questionOrder: number[];
	onAlienHandleReady?: (index: number, handle: AlienHandle | null) => void;
	onConveyorAnimationReady?: (ref: LottieRefCurrentProps | null) => void;
}

const ConveyorBelt = ({
	correctAnswersCount,
	totalQuestions,
	questionOrder,
	onAlienHandleReady,
	onConveyorAnimationReady,
}: ConveyorBeltProps) => {
	const lottieRef = useRef<LottieRefCurrentProps>(null);

	useEffect(() => {
		onConveyorAnimationReady?.(lottieRef.current);
		return () => onConveyorAnimationReady?.(null);
	}, [onConveyorAnimationReady]);

	// Calculate remaining questions
	const remainingQuestions = totalQuestions - correctAnswersCount;

	// Assign random alien variants for each question (randomized on each page reload)
	const alienAssignments = useMemo(() => {
		const shuffledAliens = [...alienAnimations].sort(() => 0.5 - Math.random());
		return Array.from({ length: totalQuestions }).map((_, index) => shuffledAliens[index % shuffledAliens.length]);
	}, [totalQuestions]);

	return (
		<div className="conveyor-belt">
			<div className="conveyor-belt__aliens">
				{questionOrder.map((questionIndex, position) => {
					const assignedAlien =
						alienAssignments[questionIndex] ?? alienAnimations[position % alienAnimations.length];
					const leftOffset = 15 - position * 40;
					return (
						<Alien
							key={`${assignedAlien.id}-${questionIndex}`}
							alien={assignedAlien}
							index={questionIndex}
							leftOffset={leftOffset}
							ref={(handle) => onAlienHandleReady?.(questionIndex, handle)}
						/>
					);
				})}
			</div>

			<div className="conveyor-belt__svg">
				<Lottie
					animationData={conveyorBeltAnimation}
					loop={false}
					autoplay={false}
					className="conveyor-belt__lottie"
					lottieRef={lottieRef}
					aria-hidden="true"
				/>
				<div className="conveyor-belt__counter">
					<ConveyorBeltCounterSvg className="conveyor-belt__counter-svg" />
					<span className="conveyor-belt__counter-number">{remainingQuestions}</span>
				</div>
			</div>
		</div>
	);
};

export default ConveyorBelt;
